// App.tsx or Dashboard.tsx
import React, { useEffect, useState } from "react";
import CaseBarChart from "./CaseBarChart";
import CasePieChart from "./CasePieChart";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import DashboardCard from "./DashboardCard";
import { useDispatch } from "react-redux";
import {
  getDashboardCardAction,
  getDashboardGraphDataAction,
} from "../../../store/reducers/dashboardSlice";
import { useSelector } from "react-redux";
import FilterDrawer from "../../../components/common/filterPannel/FilterDrawer";
import {
  BOOKING_TREND_FILTER_CONFIG,
  FILTER_CONFIG,
  FINANCIAL_FILTER_CONFIG,
} from "../../../components/common/filterPannel/FilterConfigData";
import dayjs from "dayjs";
import FormField from "../../../components/common/FormField";
import { DASHBOARD_DATEPICKER, DASHBOARD_FILTER_SOURCE } from "./DashboardUtil";
import { getAllReferrerListAction } from "../../../store/reducers/leadSlice";
import { getSurvoyerListAction } from "../../../store/reducers/surveyDiarySlice";
import CustomTable from "./CustomTable";
import CaseLineChart from "./CaseLineChart";
import CaseBookPieChart from "./Booking/CasePieChart";
import CaseFinanceBarChart from "./Finance/CaseFinanceBarChart";
import CaseFinanceLineChart from "./Finance/CaseFinanceLineChart";

const Dashboard: React.FC = () => {
  const cardData = [
    { title: "Total Leads", key: "total_leads" },
    { title: "Total Job Leads", key: "total_job_leads" },
    { title: "Total Completed Leads", key: "total_completed_leads" },
    { title: "Total Customers", key: "total_customers" },
    { title: "Total Referrals", key: "total_referrers" },
    { title: "Total Job Schedules", key: "total_job_schedules" },
    { title: "Total Users", key: "total_staffs" },
    { title: "Total Invoices Generated", key: "total_invoices_generated" },
  ];
  const [filterOption, setFilterOption] = useState<any>({
    page: 1,
    limit: 10,
    orderBy: "created_at",
    orderIn: "DESC",
    key: "",
  });
  const { dashboardCards, graphData } = useSelector(
    (state: any) => state.dashboard
  );

  const { referrers } = useSelector((state: any) => state.lead);
  const { surveyorList } = useSelector((state: any) => state?.surveyDiary);

  useEffect(() => {
    dispatch(getAllReferrerListAction(filterOption));
    dispatch(getSurvoyerListAction());
  }, []);

  const [filterCardFormData, setFilterCardFormData] = useState<any>({
    startDateRange: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    endDateRange: dayjs().format("YYYY-MM-DD"),
    excLeadStatus: true,
  });

  const [filterFormData, setFilterFormData] = useState<any>({
    source: "LEAD",
    startDateRange: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    endDateRange: dayjs().format("YYYY-MM-DD"),
    interval: "day",
    invoiceType: "",
    typeOfMove: "",
    refName: "",
    fromStatus: "",
    toStatus: "",
    location: "",
    postCode: "",
    country: "",
    serviceName: "",
    includeArchived: false,
  });

  const [filterOpen, setFilterOpen] = useState(false);

  const [tabIndex, setTabIndex] = React.useState(0);
  const dispatch = useDispatch();
  // Fetch Dashboard Cards
  useEffect(() => {
    if (tabIndex === 0) {
      dispatch(getDashboardCardAction(filterFormData));
      dispatch(getDashboardGraphDataAction(filterFormData));
    }
  }, [tabIndex, filterFormData, dispatch]);

  const [sourceType, setSourceType] = useState("LEAD");

  const sourceTypeHandler = (e: any) => {
    const updatedFilterData = {
      ...filterFormData,
      source: e.target.value,
      startDateRange: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      endDateRange: dayjs().format("YYYY-MM-DD"),
      interval: "day",
      invoiceType: "",
      typeOfMove: "",
      refName: "",
      fromStatus: "",
      toStatus: "",
      location: "",
      postCode: "",
      country: "",
      serviceName: "",
    };

    setSourceType(e.target.value);
    setFilterFormData(updatedFilterData);
    dispatch(getDashboardGraphDataAction(updatedFilterData));
  };

  const handleViewModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: string
  ) => {
    const updatedFilterData = {
      ...filterFormData,
      showStats: false,
      interval: newView,
    };
    setFilterFormData(updatedFilterData);
    dispatch(getDashboardGraphDataAction(updatedFilterData));
  };

  const handleFilterApply = (filters: Record<string, string | string[]>) => {
    const updatedFilterFormData = {
      ...filterFormData,
      showStats: false,
      refName: (filters?.referrer as string[])?.[0] || "", // handle single selection
      location: filters?.location || "",
      postCode: filters?.postCode || "",
      country: filters?.country || "",
      invoiceType: (filters?.invoiceType as string[])?.[0] || "",
      typeOfMove: (filters?.sectorType as string[])?.[0] || "",
      fromStatus: (filters?.statusFrom as string[]) || "",
      toStatus: (filters?.statusTo as string[]) || "",
      serviceName: (filters?.serviceName as string[])?.[0] || "",

      // Add more mappings here if your filter config includes more
    };

    setFilterFormData(updatedFilterFormData);
    dispatch(getDashboardGraphDataAction(updatedFilterFormData));
    setFilterOpen(false);
  };

  const handleDateRangeChange = (event: any) => {
    const { id, value } = event.target;
    // let dateRange: [string, string] | null = null;
    if (Array.isArray(value) && value.length === 2) {
      const [startDate, endDate] = value;
      setFilterFormData((prev: any) => ({
        ...prev,
        startDateRange: dayjs(startDate).format("YYYY-MM-DD"),
        endDateRange: dayjs(endDate).format("YYYY-MM-DD"),
        showStats: false,
      }));
      dispatch(
        getDashboardGraphDataAction({
          ...filterFormData,
          startDateRange: dayjs(startDate).format("YYYY-MM-DD"),
          endDateRange: dayjs(endDate).format("YYYY-MM-DD"),
        })
      );
    }
  };

  const handleCardDateRangeChange = (event: any) => {
    const { id, value } = event.target;
    // let dateRange: [string, string] | null = null;
    if (Array.isArray(value) && value.length === 2) {
      const [startDate, endDate] = value;
      setFilterCardFormData({
        ...filterCardFormData,
        // excLeadStatus: true,
        startDateRange: dayjs(startDate).format("YYYY-MM-DD"),
        endDateRange: dayjs(endDate).format("YYYY-MM-DD"),
        showStats: false,
      });
      dispatch(
        getDashboardCardAction({
          ...filterCardFormData,
          // excLeadStatus: true,
          startDateRange: dayjs(startDate).format("YYYY-MM-DD"),
          endDateRange: dayjs(endDate).format("YYYY-MM-DD"),
        })
      );
    }
  };

  const handleSwitchToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFilterFormData((prev: any) => ({
      ...prev,
      includeArchived: isChecked,
    }));
    dispatch(
      getDashboardGraphDataAction({
        ...filterFormData,
        includeArchived: isChecked,
      })
    );
  };

  // toggles the existing excLeadStatus and dispatches with updated value
  const handleCardArchiveToggle = () => {
    const newVal = !filterCardFormData?.excLeadStatus; // same semantics as before
    const updated = { ...filterCardFormData, excLeadStatus: newVal };
    setFilterCardFormData(updated);
    dispatch(getDashboardCardAction(updated));
  };

  return (
    <>
      {/* Top Header with Filter Button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1}
        sx={{
          alignItems: "center",
          justifyItems: "center",
          marginBottom: "28px",
          
        }}
      >
        {/* <Typography variant="h5">Dashboard</Typography> */}
        <Box display="flex" justifyContent="center">
          <Tabs
            value={tabIndex}
            onChange={(_, newIndex) => setTabIndex(newIndex)}
            centered
            TabIndicatorProps={{ style: { display: "none" } }} // ✅ Hide default underline
            sx={{
              backgroundColor: "#f9f9f9",
              borderRadius: "12px",
              padding: "4px",
              minHeight: "40px",
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                minHeight: "32px",
                minWidth: "120px",
                borderRadius: "8px",
                color: "#333",
              },
              "& .Mui-selected": {
                backgroundColor: "#d1fae5", // ✅ Soft green
                color: "#065f46", // ✅ Dark green text
              },
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Reports" />
          </Tabs>
        </Box>
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          mb={1}
          gap={2}
          sx={{ width: "300px", marginRight: "60px" }}
        >
          {/* -- Pill (clickable) -- */}
          <Box
            role="button"
            tabIndex={0}
            onClick={handleCardArchiveToggle}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCardArchiveToggle();
              }
            }}
            aria-pressed={!filterCardFormData?.excLeadStatus} // isOn = !excLeadStatus
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              borderRadius: "25px",
              padding: "4px 10px",
              cursor: "pointer",
              userSelect: "none",
              // dynamic look: green look when ON, neutral when OFF
              border: (theme) =>
                !filterCardFormData?.excLeadStatus
                  ? `1px solid #3CD4A0` // when isOn === true
                  : `1px solid #d1d5db`,
              backgroundColor: !filterCardFormData?.excLeadStatus
                ? "#91e8c9ff"
                : "#ffffff",
              color: !filterCardFormData?.excLeadStatus ? "#056a57" : "#374151",
              // small shadow for hover
              "&:hover": {
                backgroundColor: !filterCardFormData?.excLeadStatus
                  ? "#79e6be"
                  : "#f3f4f6",
              },
              outline: "none",
              // "&:focus": {
              //   boxShadow: (t) => `0 0 0 3px ${t.palette.action.selected}`,
              // },
            }}
          >
            <Typography
              sx={{ fontSize: "12px", fontWeight: 600 }}
              variant="caption"
              color="inherit"
            >
              Include Archived:
            </Typography>

            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                // visually separate state text
                ml: 0,
                color: "inherit",
              }}
              variant="caption"
            >
              {/* isOn = !excLeadStatus */}
              {!filterCardFormData?.excLeadStatus ? "On" : "Off"}
            </Typography>

            {/* Hidden checkbox for screen readers (keeps semantic input available) */}
            <input
              aria-hidden="true"
              type="checkbox"
              checked={!filterCardFormData?.excLeadStatus}
              onChange={() => {
                /* keep input change in sync — call same handler */
                handleCardArchiveToggle();
              }}
              style={{
                // visually hidden but available to assistive tech if needed
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                border: 0,
              }}
            />
          </Box>

          {DASHBOARD_DATEPICKER?.map((field, i) => (
            <>
              <FormField
                key={field.id}
                onPaste={(e: any) => e.preventDefault()}
                onKeyDown={(event: any) => {
                  if (
                    [8, 9, 37, 38, 39, 40, 18, 17].includes(event.keyCode) ||
                    event.ctrlKey
                  )
                    return event;
                }}
                {...{
                  ...field,
                  value: [
                    filterCardFormData.startDateRange,
                    filterCardFormData.endDateRange,
                  ],
                  // minDate: field.id === "movingOnDate" ? new Date() : undefined,
                  // maxDate:
                  //   field.id === "followUpDate"
                  //     ? newLeadLocal?.movingOnDate &&
                  //       dayjs(newLeadLocal.movingOnDate)
                  //         .toDate()
                  //     : undefined,
                  variant: "outlined",
                  onChange: handleCardDateRangeChange,
                  // onBlur: onBlurAction,
                  width: "280px",
                  iconCalender: "16px",
                  heightVal: "26px",
                  isNonMarginTop: true,
                  isNonMarginBottom: true,
                  InputProps: {
                    // ...(field?.inputProps && field?.inputProps),

                    sx:
                      field.type === "text" || field.type === "password"
                        ? { height: "24px" }
                        : {
                            padding: "2px 0px",
                            height: "24px",
                            width: "100%",

                            marginTop: "10px",
                          },
                  },
                }}
              />
            </>
          ))}
        </Box>
      </Box>
      <Grid
        container
        rowSpacing={1}
        columnSpacing={{ xs: 1, sm: 2, md: 12 }}
        // sx={{ height: "100vh" }}
      >
        {tabIndex === 0 && (
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              paddingLeft: "10px !important",
              padding: "0px 10px !important",
            }}
          >
            <Grid container spacing={2}>
              {cardData.map((item, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={index}
                  sx={{ paddingLeft: "10px !important" }}
                >
                  <DashboardCard
                    dashboardVal={dashboardCards}
                    title={item.title}
                    count={Number(dashboardCards?.[item.key]) || 0}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {tabIndex === 0 && (
          <>
            <Grid
              item
              xs={12}
              md={12}
              sx={{
                padding: "2px 8px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                // justifyContent: "end",
                width: "100%",
                paddingLeft: "8px !important",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                gap={2}
              >
                <ToggleButtonGroup
                  value={filterFormData?.interval}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                  sx={{ fontSize: "12px", color: "#666", padding: "2px 6px" }}
                >
                  <ToggleButton
                    sx={{ fontSize: "12px", color: "#666", padding: "2px 6px" }}
                    value="day"
                  >
                    Day
                  </ToggleButton>
                  <ToggleButton
                    sx={{ fontSize: "12px", color: "#666", padding: "2px 6px" }}
                    value="week"
                  >
                    Week
                  </ToggleButton>
                  <ToggleButton
                    sx={{ fontSize: "12px", color: "#666", padding: "2px 6px" }}
                    value="month"
                  >
                    Month
                  </ToggleButton>
                </ToggleButtonGroup>

                {DASHBOARD_DATEPICKER?.map((field, i) => (
                  <>
                    <FormField
                      key={field.id}
                      onPaste={(e: any) => e.preventDefault()}
                      onKeyDown={(event: any) => {
                        if (
                          [8, 9, 37, 38, 39, 40, 18, 17].includes(
                            event.keyCode
                          ) ||
                          event.ctrlKey
                        )
                          return event;
                      }}
                      {...{
                        ...field,
                        value: [
                          filterFormData.startDateRange,
                          filterFormData.endDateRange,
                        ],

                        variant: "outlined",

                        onChange: handleDateRangeChange,
                        // onBlur: onBlurAction,
                        iconCalender: "16px",
                        width: "280px",
                        heightVal: "26px",
                        isNonMarginTop: true,
                        isNonMarginBottom: true,
                        InputProps: {
                          // ...(field?.inputProps && field?.inputProps),

                          sx:
                            field.type === "text" || field.type === "password"
                              ? { height: "24px" }
                              : {
                                  padding: "2px 0px",
                                  height: "24px",
                                  width: "360px",
                                  marginTop: "10px",
                                },
                        },
                      }}
                    />
                  </>
                ))}

                <Button
                  sx={{
                    padding: "2px 8px",
                    fontSize: "12px",
                    marginLeft: "80px",
                  }}
                  variant="outlined"
                  onClick={() => setFilterOpen(true)}
                >
                  Filter
                </Button>
              </Box>
              {/* ✅ Switch Toggle */}
              <Box
                role="button"
                tabIndex={0}
                onClick={() =>
                  handleSwitchToggle({
                    target: { checked: !filterFormData?.includeArchived },
                  } as any)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSwitchToggle({
                      target: { checked: !filterFormData?.includeArchived },
                    } as any);
                  }
                }}
                aria-pressed={filterFormData?.includeArchived}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "25px",
                  padding: "4px 10px",
                  cursor: "pointer",
                  userSelect: "none",
                  height: "20px",
                  border: filterFormData?.includeArchived
                    ? "1px solid #3CD4A0"
                    : "1px solid #d1d5db",
                  backgroundColor: filterFormData?.includeArchived
                    ? "#91e8c9ff"
                    : "#ffffff",
                  color: filterFormData?.includeArchived
                    ? "#056a57"
                    : "#374151",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: filterFormData?.includeArchived
                      ? "#79e6be"
                      : "#f3f4f6",
                  },
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600 }}
                  variant="caption"
                  color="inherit"
                >
                  Include Archived:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "inherit",
                  }}
                  variant="caption"
                >
                  {filterFormData?.includeArchived ? "On" : "Off"}
                </Typography>
              </Box>
              {DASHBOARD_FILTER_SOURCE?.map((field, i) => (
                <>
                  <FormField
                    key={field.id}
                    onPaste={(e: any) => e.preventDefault()}
                    onKeyDown={(event: any) => {
                      if (
                        [8, 9, 37, 38, 39, 40, 18, 17].includes(
                          event.keyCode
                        ) ||
                        event.ctrlKey
                      )
                        return event;
                    }}
                    {...{
                      ...field,
                      value: sourceType,
                      // minDate: field.id === "movingOnDate" ? new Date() : undefined,
                      // maxDate:
                      //   field.id === "followUpDate"
                      //     ? newLeadLocal?.movingOnDate &&
                      //       dayjs(newLeadLocal.movingOnDate)
                      //         .toDate()
                      //     : undefined,
                      variant: "outlined",
                      iconCalender: "16px",
                      onChange: sourceTypeHandler,
                      // onBlur: onBlurAction,
                      width: "300px",
                      isNonMarginTop: true,
                      InputProps: {
                        // ...(field?.inputProps && field?.inputProps),

                        sx:
                          field.type === "text" || field.type === "password"
                            ? { height: "24px" }
                            : { padding: "2px 0px", height: "24px" },
                      },
                    }}
                  />
                </>
              ))}
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ padding: "16px 20px", paddingLeft: "20px !important" }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: "100%",
                  background: "#fff",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <h4 style={{ marginBottom: "10px" }}>
                  {filterFormData?.source === "LEAD"
                    ? "Lead"
                    : filterFormData?.source === "BOOKING"
                    ? "Booking"
                    : "Total Payment"}{" "}
                  Summary -{" "}
                  {filterFormData?.source === "LEAD" ? "Total Leads" : ""}
                </h4>

                {filterFormData?.source === "LEAD" && (
                  <CaseBarChart
                    viewMode={filterFormData?.interval}
                    chartsData={
                      filterFormData?.source === "LEAD"
                        ? graphData?.by_interval
                        : graphData?.trends
                    }
                  />
                )}
                {filterFormData?.source === "BOOKING" && (
                  <>
                    <CaseLineChart
                      chartsData={graphData?.trends}
                      viewMode={filterFormData?.interval}
                    />
                  </>
                )}
                {filterFormData?.source === "FINANCE" && (
                  <CaseFinanceLineChart
                    chartsData={graphData?.revenue_collected}
                    viewMode={filterFormData?.interval}
                  />
                )}
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ padding: "16px 20px", paddingLeft: "20px !important" }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: "400px",
                  background: "#fff",
                  padding: "16px",
                  borderRadius: "8px",
                }}
              >
                <h4 style={{ marginBottom: "10px" }}>
                  {filterFormData?.source === "LEAD"
                    ? "Lead"
                    : filterFormData?.source === "BOOKING"
                    ? "Booking Status Transition"
                    : "Invoice"}{" "}
                  Summary - By Status
                </h4>

                {filterFormData?.source === "LEAD" ? (
                  <CasePieChart pieData={graphData?.by_status} />
                ) : filterFormData?.source === "BOOKING" ? (
                  <CaseBookPieChart pieData={graphData?.status_transition} />
                ) : (
                  <CaseFinanceBarChart
                    chartsData={graphData?.status_data}
                    viewMode={filterFormData?.interval}
                  />
                )}
              </div>
            </Grid>
          </>
        )}
        {tabIndex === 1 && (
          <Grid
            item
            xs={12}
            md={12}
            sx={{
              paddingLeft: "10px !important",
              padding: "2px 8px",
              display: "flex",
              justifyContent: "space-between",
              // justifyContent: "end",
              width: "100%",
            }}
          >
            <CustomTable />
          </Grid>
        )}
      </Grid>
      {/* Filter Drawer */}
      <FilterDrawer
        title={sourceType ?? "Filter Dashboard"}
        open={filterOpen}
        sourceType={sourceType}
        referrers={referrers}
        surveyorList={surveyorList}
        onClose={() => setFilterOpen(false)}
        onApply={handleFilterApply}
        filtersConfig={
          sourceType === "LEAD"
            ? FILTER_CONFIG
            : sourceType === "BOOKING"
            ? BOOKING_TREND_FILTER_CONFIG
            : sourceType === "FINANCE"
            ? FINANCIAL_FILTER_CONFIG
            : []
        }
      />
    </>
  );
};

export default Dashboard;
